import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarRange, CalendarCheck, Users,
  MessagesSquare, Palette, MonitorSmartphone, Inbox, Landmark, Target,
  FolderOpen, Images, CheckSquare, Plus, Zap, Wrench, ChevronDown,
} from "lucide-react";

/* Design System DGA — sidebar bleu nuit (référence maquettes).
   Les 12 rubriques principales suivent l'ordre exact demandé ; les modules
   historiques hors maquette restent accessibles dans le groupe « Outils ». */
export const NAV_MAIN = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  // Le calendrier éditorial vit désormais dans la page Community Management (onglet Calendrier)
  { to: "/agenda", label: "Agenda", icon: CalendarRange },
  { to: "/taches", label: "To-do", icon: CheckSquare },
  { to: "/rdv", label: "Rendez-vous", icon: CalendarCheck, countKey: "rdv", quick: true },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/cm", label: "Community Management", icon: MessagesSquare },
  { to: "/cm/pilotage", label: "Pilotage de contenu", icon: Images },
  { to: "/graphisme", label: "Graphisme", icon: Palette },
  { to: "/sites-web", label: "Web Design", icon: MonitorSmartphone },
  { to: "/demandes", label: "Demandes", icon: Inbox, countKey: "demandes" },
  { to: "/comptabilite", label: "Comptabilité", icon: Landmark },
  { to: "/prospection", label: "Prospection", icon: Target },
  // La rentabilité agence vit désormais dans Comptabilité → onglet « Rentabilité »
];

/* Fonctionnalités existantes conservées — regroupées dans le sous-menu « Outils ».
   « Contenus » et « Idées IA » sont retirés du menu : ils vivent dans la partie
   Community Management (fiche client). */
export const NAV_TOOLS = [
  { to: "/graphisme/documents", label: "Documents", icon: FolderOpen },
  { to: "/medias", label: "Bibliothèque média", icon: Images },
];

const itemCls = (isActive) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm mb-0.5 transition-colors duration-150 ${
    isActive
      ? "bg-[#1C2A4F] text-white font-medium"
      : "text-[#8E9BBA] hover:text-white hover:bg-[#141E3C]"}`;

function NavItem({ item, counts, onQuickRdv, onNavigate }) {
  const { to, label, icon: Icon, end, countKey, quick, noActive } = item;
  const count = countKey ? counts[countKey] || 0 : 0;
  const testid = `nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`;
  // Lien provisoire sans état actif (ni visuel ni aria-current) — ex. CM → /clients avant Phase H
  if (noActive) {
    return (
      <Link to={to} data-testid={testid} onClick={onNavigate} className={itemCls(false)}>
        <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.8} />
        <span className="truncate">{label}</span>
      </Link>
    );
  }
  return (
    <NavLink to={to} end={end} data-testid={testid} onClick={onNavigate}
      className={({ isActive }) => itemCls(noActive ? false : isActive)}>
      <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.8} />
      <span className="truncate">{label}</span>
      {count > 0 && (
        <span className="ml-auto bg-[#FFE4E6] text-[#BE123C] text-[10px] font-semibold rounded-full px-1.5 py-0.5"
          data-testid={countKey === "rdv" ? "nav-rdv-count" : "nav-demandes-count"}>
          {count}
        </span>
      )}
      {quick && (
        <span data-testid="nav-rdv-new" role="button" title="Nouveau rendez-vous"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuickRdv(); }}
          className={`${count > 0 ? "ml-1.5" : "ml-auto"} w-5 h-5 rounded-md flex items-center justify-center text-[#8E9BBA] hover:text-white hover:bg-[#1C2A4F] transition-colors`}>
          <Plus className="w-3.5 h-3.5" />
        </span>
      )}
    </NavLink>
  );
}

export default function AppSidebar({ brand, counts, onQuickRdv, onNavigate }) {
  const location = useLocation();
  const toolsActive = NAV_TOOLS.some((t) => location.pathname.startsWith(t.to));
  const [toolsOpen, setToolsOpen] = useState(toolsActive);
  useEffect(() => { if (toolsActive) setToolsOpen(true); }, [toolsActive]);
  return (
    <div className="flex flex-col h-full bg-[#0B132B] text-white" data-testid="app-sidebar">
      {/* Logo agence */}
      <div className="px-5 pt-6 pb-7 flex items-center gap-2.5">
        {brand.app_logo_url ? (
          <img src={brand.app_logo_url} alt="" className="w-9 h-9 rounded-lg object-cover border border-white/10" data-testid="app-logo-img" />
        ) : (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#1C2A4F]">
            <Zap className="w-4 h-4 text-white" />
          </div>
        )}
        <div className="leading-tight">
          <p className="font-heading font-bold text-xl tracking-wide" data-testid="app-logo">{brand.app_name}</p>
          <p className="text-[9px] uppercase tracking-[0.25em] text-[#8E9BBA]">— Agence —</p>
        </div>
      </div>

      {/* Navigation principale */}
      <nav className="flex-1 overflow-y-auto px-3" data-testid="app-sidebar-nav">
        {NAV_MAIN.map((item) => (
          <NavItem key={item.label} item={item} counts={counts} onQuickRdv={onQuickRdv} onNavigate={onNavigate} />
        ))}

        <button data-testid="nav-outils-toggle" onClick={() => setToolsOpen((o) => !o)}
          className="w-full flex items-center gap-2 px-3 mt-6 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7E8C9D] hover:text-white transition-colors">
          <Wrench className="w-3.5 h-3.5" />
          <span>Outils</span>
          <ChevronDown className={`w-3 h-3 ml-auto transition-transform duration-200 ${toolsOpen ? "" : "-rotate-90"}`} />
        </button>
        {toolsOpen && NAV_TOOLS.map((item) => (
          <NavItem key={item.label} item={item} counts={counts} onQuickRdv={onQuickRdv} onNavigate={onNavigate} />
        ))}
      </nav>

      {/* Baseline agence (dégagée du widget minuteur flottant) */}
      <div className="px-5 pt-5 pb-24 border-t border-white/[0.08]">
        <p className="text-[10px] uppercase tracking-[0.2em] leading-relaxed text-[#7E8C9D]">
          Créer<br />Développer<br />Faire briller<br />vos idées
        </p>
      </div>
    </div>
  );
}
